import React, { useMemo } from 'react';
import { CircleNotchIcon, PlusIcon, XIcon } from '@phosphor-icons/react';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import { CoreEnumOptionDTO } from 'src/api/core/core-enums/useCoreEnums';
import { CoreEnumOptionAssociation } from 'src/api/core/enumOptionAssociations/useEnumOptionAssociations';

interface TagAssociation {
  id: string;
  coreEnumOptionId: string;
  coreEnumOption?: {
    id: string;
    name: string | null;
    description: string | null;
    sortOrder: number;
    enumId: string;
  };
}

/**
 * Hook to extract tag name from a tag association
 * Memoized for performance
 */
export function useTagName(
  tag: TagAssociation | CoreEnumOptionAssociation,
  availableTags: CoreEnumOptionDTO[],
  fallback = 'Unknown',
): string {
  return useMemo(() => {
    return (
      tag.coreEnumOption?.name ||
      availableTags.find((opt) => opt.id === tag.coreEnumOptionId)?.name ||
      fallback
    );
  }, [tag.coreEnumOption?.name, tag.coreEnumOptionId, availableTags, fallback]);
}

interface TagBadgeProps {
  tag: TagAssociation | CoreEnumOptionAssociation;
  availableTags: CoreEnumOptionDTO[];
  variant?: 'default' | 'secondary' | 'outline';
  size?: 'sm' | 'md';
  showRemove?: boolean;
  onRemove?: (tagId: string) => void;
  isUpdating?: boolean;
  className?: string;
  fallbackName?: string;
}

/**
 * Reusable component for displaying a tag badge
 */
export function TagBadge({
  tag,
  availableTags,
  variant = 'default',
  size = 'md',
  showRemove = false,
  onRemove,
  isUpdating = false,
  className,
  fallbackName = 'Unknown',
}: TagBadgeProps) {
  const tagName = useTagName(tag, availableTags, fallbackName);

  const sizeClasses =
    size === 'sm' ? 'gap-1 pl-2 pr-1 text-xs' : 'gap-2 pl-3 pr-2';
  const iconSize = size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3';

  return (
    <Badge variant={variant} className={`${sizeClasses} ${className || ''}`}>
      <span>{tagName}</span>
      {showRemove && onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove(tag.coreEnumOptionId);
          }}
          disabled={isUpdating}
          className="inline-flex hover:opacity-70 transition-opacity"
          title="Remove tag"
        >
          <XIcon className={iconSize} />
        </button>
      )}
    </Badge>
  );
}

export function StaticTagBadge({ tag }: { tag: string }) {
  return (
    <Badge
      variant="secondary"
      className="gap-1 pl-2 pr-1 text-xs border-dashed border"
    >
      {tag}
    </Badge>
  );
}
interface TagsDisplayProps {
  currentTags: TagAssociation[];
  availableTags: CoreEnumOptionDTO[];
  currentTagIds: Set<string>;
  isLoading: boolean;
  isUpdating: boolean;
  maxTags?: number;
  onAddTag: (tagId: string) => void;
  onRemoveTag: (tagId: string) => void;
  layout?: 'inline' | 'compact'; // 'inline' for header/modal, 'compact' for rule cards
  showEmptyState?: boolean;
  staticTags?: string[];
  lockedTagIds?: Set<string>; // Tag IDs that cannot be added or removed
}

export function TagsDisplay({
  currentTags,
  availableTags,
  currentTagIds,
  isLoading,
  isUpdating,
  maxTags,
  onAddTag,
  onRemoveTag,
  layout = 'compact',
  showEmptyState = true,
  staticTags,
  lockedTagIds = new Set(),
}: TagsDisplayProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <CircleNotchIcon className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading tags...</span>
      </div>
    );
  }

  const maxTagCount = maxTags ?? Infinity;
  const canAddMore = currentTags.length < maxTagCount;

  // Filter out locked tags from available tags (users can't add them)
  const unappliedTags = availableTags.filter(
    (tag) => !currentTagIds.has(tag.id) && !lockedTagIds.has(tag.id),
  );

  const handleAddTag = (tagId: string) => {
    if (!canAddMore) return;
    onAddTag(tagId);
  };

  if (layout === 'inline') {
    // For header and modal: show applied tags and available tags as separate sections
    return (
      <div className="space-y-3">
        {/* Applied Tags - Regular tags and static tags inline */}
        {currentTags.length > 0 || (staticTags && staticTags.length > 0) ? (
          <div className="flex flex-wrap gap-2">
            {currentTags.map((tag) => {
              const isLocked = lockedTagIds.has(tag.coreEnumOptionId);
              return (
                <TagBadge
                  key={tag.id}
                  tag={tag}
                  availableTags={availableTags}
                  variant="default"
                  size="md"
                  showRemove={!isLocked}
                  onRemove={!isLocked ? onRemoveTag : undefined}
                  isUpdating={isUpdating}
                  fallbackName="Unknown Tag"
                />
              );
            })}
            {staticTags?.map((tag) => (
              <StaticTagBadge key={tag} tag={tag} />
            ))}
          </div>
        ) : (
          showEmptyState && (
            <p className="text-sm text-muted-foreground">No tags applied</p>
          )
        )}

        {/* Available Tags to Add */}
        {availableTags.length > 0 && unappliedTags.length > 0 && canAddMore && (
          <div className="flex flex-wrap gap-2">
            {unappliedTags.map((tag) => (
              <Button
                key={tag.id}
                variant="outline"
                size="sm"
                onClick={() => handleAddTag(tag.id)}
                disabled={isUpdating}
                className="gap-2"
              >
                <PlusIcon className="h-3 w-3" />
                {tag.name}
              </Button>
            ))}
          </div>
        )}

        {availableTags.length === 0 && currentTags.length === 0 && (
          <div className="p-3 bg-muted rounded-lg text-center">
            <p className="text-sm text-muted-foreground">
              No tags available. Create tags in organization settings to use
              them here.
            </p>
          </div>
        )}
      </div>
    );
  }

  // Compact layout: for rule cards - inline with applied tags and dropdown for adding
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Applied Tags */}
      {currentTags.map((tag) => {
        const isLocked = lockedTagIds.has(tag.coreEnumOptionId);
        return (
          <TagBadge
            key={tag.id}
            tag={tag}
            availableTags={availableTags}
            variant="default"
            size="sm"
            showRemove={!isLocked}
            onRemove={!isLocked ? onRemoveTag : undefined}
            isUpdating={isUpdating}
            fallbackName="Unknown"
          />
        );
      })}
      {staticTags?.map((tag) => (
        <StaticTagBadge key={tag} tag={tag} />
      ))}
      {/* Add Tag Button - Dropdown */}
      {unappliedTags.length > 0 && canAddMore && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
              }}
              disabled={isUpdating || availableTags.length === 0}
              className="gap-1 h-6 px-2 text-xs"
            >
              <PlusIcon className="h-3 w-3" />
              Add Tag
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            onClick={(e) => e.stopPropagation()}
          >
            {unappliedTags.map((tag) => (
              <DropdownMenuItem
                key={tag.id}
                onClick={() => handleAddTag(tag.id)}
                disabled={isUpdating}
              >
                {tag.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
